import { Fragment, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAlertesCouchages, useChambres, useNuits, useRecapNuitees, useRecapRepas, useRecapSoldes } from '../api/hooks'
import type { Categorie, Nuit, SemaineNuitees } from '../api/types'

const fmtEur = (n: number) =>
  n.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 })

const JOURS_COURT = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam']

export default function Dashboard() {
  const rn = useRecapNuitees()
  const rr = useRecapRepas()
  const rs = useRecapSoldes()
  const nuitsQ = useNuits()
  const chambresQ = useChambres()
  const alertesQ = useAlertesCouchages()

  // Familles dépliées dans « Qui doit quoi ? » (clic sur le nom du responsable)
  const [openFamilles, setOpenFamilles] = useState<Set<number>>(new Set())
  const toggleFamilleOpen = (cid: number) =>
    setOpenFamilles(s => {
      const n = new Set(s)
      n.has(cid) ? n.delete(cid) : n.add(cid)
      return n
    })

  // Nuits en alerte côté couchages (sur_capacite OU non_assigne) — provisoires inclus
  // car le backend leur attribue une chambre. Même règle que le sélecteur de Couchages.
  const nuitsEnSurcapacite = useMemo(() => {
    const s = new Set<number>()
    alertesQ.data?.forEach(a => {
      if ((a.type === 'sur_capacite' || a.type === 'non_assigne') && a.nuit_id != null) {
        s.add(a.nuit_id)
      }
    })
    return s
  }, [alertesQ.data])

  // Planning : confirmés et peut-être cumulés par nuit
  const planning = useMemo(() => {
    if (!nuitsQ.data) return [] as Array<{ nuit: Nuit; nb_confirmes: number; nb_peut_etre: number }>
    const conf = new Map<number, number>()
    const pe = new Map<number, number>()
    nuitsQ.data.presences.forEach(p => {
      // IMPORTANT : ignorer les refus (present=false, refus=true).
      // Sans ce filtre, ils tomberaient dans "confirmés" (tentatif=false)
      // et fausseraient les jauges du planning.
      if (!p.present) return
      if (p.tentatif) {
        pe.set(p.nuit_id, (pe.get(p.nuit_id) ?? 0) + 1)
      } else {
        conf.set(p.nuit_id, (conf.get(p.nuit_id) ?? 0) + 1)
      }
    })
    return nuitsQ.data.nuits.map(n => ({
      nuit: n,
      nb_confirmes: conf.get(n.id) ?? 0,
      nb_peut_etre: pe.get(n.id) ?? 0,
    }))
  }, [nuitsQ.data])

  const capaciteTotale = chambresQ.data?.reduce((s, c) => s + c.capacite, 0) ?? 0

  if (!rn.data || !rr.data || !rs.data) {
    return <p className="text-ocean-700 italic">Chargement…</p>
  }

  const nbPersonnesAvecNuit = rn.data.personnes.filter(p => p.nb_nuits > 0).length
  const nbPersonnesAvecRepas = rr.data.personnes.filter(p => p.nb_repas > 0).length
  const nbFamillesNuit = rn.data.comptes.filter(c => c.nb_personnes_presentes > 0).length

  // Budget hebdomadaire : exposé par le backend (fallback = division locale pour
  // rester compatible si l'API n'a pas encore le champ).
  const semaines = rn.data.semaines ?? []
  const nbSemaines = rn.data.nb_semaines ?? (semaines.length || 1)
  const coutSemaine = rn.data.cout_par_semaine ?? (rn.data.total_a_repartir / Math.max(1, nbSemaines))

  // === Index pour les colonnes Semaine 1 / Semaine 2 et les membres ==========
  // Coût net par famille et par semaine (depuis semaines[].familles — colonne K)
  const netFamilleSemaine = new Map<number, number[]>()
  semaines.forEach(sem => {
    (sem.familles ?? []).forEach(f => {
      const arr = netFamilleSemaine.get(f.compte_id) ?? []
      arr[sem.index] = f.cout_net
      netFamilleSemaine.set(f.compte_id, arr)
    })
  })
  // Coût net par personne et par semaine (depuis semaines[].detail)
  const netPersonneSemaine = new Map<number, number[]>()
  semaines.forEach(sem => {
    sem.detail.forEach(d => {
      const arr = netPersonneSemaine.get(d.personne_id) ?? []
      arr[sem.index] = d.montant_final
      netPersonneSemaine.set(d.personne_id, arr)
    })
  })
  // Repas par personne (nb + dû courses)
  const repasParPersonne = new Map(rr.data.personnes.map(p => [p.personne_id, p]))
  // Membres par famille : toutes les infos par personne pour l'expansion
  const membresParFamille = new Map<number, Array<{
    personne_id: number; prenom: string; categorie: Categorie;
    nb_nuits: number; nb_tent: number; du_maison: number; du_maison_max: number;
    par_semaine: number[]; nb_repas: number; du_repas: number;
  }>>()
  rn.data.personnes.forEach(p => {
    const rep = repasParPersonne.get(p.personne_id)
    const arr = membresParFamille.get(p.compte_id) ?? []
    arr.push({
      personne_id: p.personne_id,
      prenom: p.prenom,
      categorie: p.categorie,
      nb_nuits: p.nb_nuits,
      nb_tent: p.nb_nuits_tentatifs,
      du_maison: p.montant_du,
      du_maison_max: p.montant_du_max,
      par_semaine: netPersonneSemaine.get(p.personne_id) ?? [],
      nb_repas: rep?.nb_repas ?? 0,
      du_repas: rep?.montant_du ?? 0,
    })
    membresParFamille.set(p.compte_id, arr)
  })
  // Tri : membres actifs d'abord (avec nuits/repas), puis alphabétique
  membresParFamille.forEach(arr =>
    arr.sort((a, b) =>
      (Number(b.nb_nuits + b.nb_tent + b.nb_repas > 0) - Number(a.nb_nuits + a.nb_tent + a.nb_repas > 0))
      || a.prenom.localeCompare(b.prenom, 'fr'),
    ),
  )

  // Index par compte_id pour merger nuitées + repas + payé
  const lignes = rs.data.lignes
    .map(l => {
      const nuit = rn.data.comptes.find(c => c.compte_id === l.compte_id)
      const repas = rr.data.comptes.find(c => c.compte_id === l.compte_id)
      const du_nuitees_max = nuit?.montant_du_max ?? l.du_nuitees
      const nb_tentatifs = nuit?.nb_nuits_tentatifs_total ?? 0
      const total_du_max = du_nuitees_max + l.du_repas
      const solde_max = total_du_max - l.depenses_payees
      // Solde COURSES uniquement (entre familles) = dû courses − payé.
      // >0 la famille doit ; <0 on lui doit. La maison est réglée à part (tiers).
      const solde_courses = l.solde_courses ?? (l.du_repas - l.depenses_payees)
      return {
        compte_id: l.compte_id,
        compte_nom: l.compte_nom,
        nb_nuits: nuit?.nb_nuits_total ?? 0,
        nb_personnes_presentes: nuit?.nb_personnes_presentes ?? 0,
        du_nuitees: l.du_nuitees,
        du_nuitees_max,
        nb_tentatifs,
        nb_repas: repas?.nb_repas_total ?? 0,
        du_repas: l.du_repas,
        depenses_payees: l.depenses_payees,
        total_du: l.total_du,
        total_du_max,
        solde: l.solde,
        solde_max,
        solde_courses,
      }
    })
    .filter(l => l.nb_nuits > 0 || l.nb_repas > 0 || l.depenses_payees > 0 || l.nb_tentatifs > 0)

  // Totaux pour la ligne TOTAL du tableau
  const totaux = lignes.reduce((acc, l) => ({
    nb_nuits: acc.nb_nuits + l.nb_nuits,
    nb_tentatifs: acc.nb_tentatifs + l.nb_tentatifs,
    nb_repas: acc.nb_repas + l.nb_repas,
    du_nuitees: acc.du_nuitees + l.du_nuitees,
    du_nuitees_max: acc.du_nuitees_max + l.du_nuitees_max,
    du_repas: acc.du_repas + l.du_repas,
    depenses_payees: acc.depenses_payees + l.depenses_payees,
    total_du: acc.total_du + l.total_du,
    total_du_max: acc.total_du_max + l.total_du_max,
    solde: acc.solde + l.solde,
    solde_max: acc.solde_max + l.solde_max,
    solde_courses: acc.solde_courses + l.solde_courses,
  }), {
    nb_nuits: 0, nb_tentatifs: 0, nb_repas: 0,
    du_nuitees: 0, du_nuitees_max: 0, du_repas: 0,
    depenses_payees: 0,
    total_du: 0, total_du_max: 0,
    solde: 0, solde_max: 0, solde_courses: 0,
  })

  return (
    <div className="space-y-5 sm:space-y-6">
      {/* Bloc d'intro pédagogique : comment ça marche + 4 états nuitées */}
      <section className="card bg-gradient-sand p-4 sm:p-5 text-sand-800">
        <h3 className="font-bold mb-2 text-base sm:text-lg">💡 Comment ça marche</h3>
        <p className="text-sm mb-2">
          Voici un <strong>aperçu en temps réel</strong> du séjour. Tout se met à jour
          automatiquement quand chacun coche ses présences, ses repas ou enregistre une dépense.
        </p>
        <ul className="list-none space-y-1.5 text-sm">
          <li className="grid grid-cols-[26px_1fr] items-start gap-2">
            <span aria-hidden className="text-base leading-none">🌙</span>
            <span>
              <strong>Nuitées</strong> — chacun coche ses nuits. <strong>4 états</strong> par cellule :
              <span className="ml-1 inline-flex items-center flex-wrap gap-1">
                <span className="inline-flex items-center justify-center w-5 h-5 rounded border border-ocean-200 bg-white text-ocean-400 text-[10px]">·</span>vide (pas répondu),
                <span className="inline-flex items-center justify-center w-5 h-5 rounded border border-ocean-600 bg-gradient-ocean text-white text-[10px] font-bold">✓</span>confirmé (compté dans le prix),
                <span className="inline-flex items-center justify-center w-5 h-5 rounded border border-amber-400 bg-amber-200 text-amber-800 text-[10px] font-bold">↔</span>provisoire (peut se retirer si trop de monde),
                <span className="inline-flex items-center justify-center w-5 h-5 rounded border border-coral-600 bg-coral-500 text-white text-[10px] font-bold">✕</span>ne vient pas.
              </span>
            </span>
          </li>
          <li className="grid grid-cols-[26px_1fr] items-start gap-2">
            <span aria-hidden className="text-base leading-none">🛏️</span>
            <span><strong>Couchages</strong> — une chambre est attribuée automatiquement dès qu'on coche une présence ; modifiable à la main.</span>
          </li>
          <li className="grid grid-cols-[26px_1fr] items-start gap-2">
            <span aria-hidden className="text-base leading-none">🍽️</span>
            <span><strong>Repas</strong> — chacun coche les repas pris (Midi/Soir). Les courses sont divisées par le nombre total de repas.</span>
          </li>
          <li className="grid grid-cols-[26px_1fr] items-start gap-2">
            <span aria-hidden className="text-base leading-none">🛒</span>
            <span><strong>Dépenses</strong> — qui a payé quoi pour les courses.</span>
          </li>
          <li className="grid grid-cols-[26px_1fr] items-start gap-2">
            <span aria-hidden className="text-base leading-none">🧮</span>
            <span><strong>Comptes</strong> — le <strong>dû maison</strong> se règle au propriétaire, et les <strong>courses</strong> se remboursent entre familles (semaine par semaine).</span>
          </li>
        </ul>
      </section>

      {/* Cartes de stats */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          label={nbFamillesNuit > 1 ? 'Familles présentes' : 'Famille présente'}
          value={`${nbFamillesNuit}`}
          sub={`${nbPersonnesAvecNuit} pers. avec nuitée${nbPersonnesAvecNuit > 1 ? 's' : ''}`}
          accent="ocean"
          icon="👨‍👩‍👧"
        />
        <StatCard
          label="Coût maison à répartir"
          value={`${fmtEur(coutSemaine)} / semaine`}
          sub={
            rn.data.prix_par_poids > 0
              ? `${fmtEur(rn.data.prix_par_poids)}/nuit adulte · ${nbSemaines} sem.`
              : `${nbSemaines} semaine${nbSemaines > 1 ? 's' : ''} · aucune présence`
          }
          accent="coral"
          icon="🏠"
        />
        <StatCard
          label="Courses"
          value={fmtEur(rr.data.total_depenses)}
          sub={`${nbPersonnesAvecRepas} pers. · ${rr.data.nb_repas_total} repas`}
          accent="sand"
          icon="🛒"
        />
        <StatCard
          label="Prix / repas"
          value={rr.data.prix_par_repas > 0 ? fmtEur(rr.data.prix_par_repas) : '—'}
          sub="hors bébés"
          accent="sunset"
          icon="🍽️"
        />
      </section>

      {/* Récap qui doit quoi */}
      <section className="card overflow-hidden">
        <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-ocean-100/60 flex items-start justify-between gap-2">
          <div>
            <h2 className="font-display text-lg sm:text-xl font-bold text-ocean-900">
              Qui doit quoi ?
            </h2>
            <p className="text-xs text-ocean-700">
              Mis à jour en temps réel d'après les présences et dépenses saisies.
            </p>
            <p className="text-[11px] text-ocean-700 mt-1 leading-snug">
              <span className="font-semibold text-coral-700">🏠 Maison</span> — versée par chaque famille
              <strong> au propriétaire</strong> (un tiers), au prorata des nuits.
              <span className="mx-1">·</span>
              <span className="font-semibold text-emerald-700">🛒 Courses</span> — réglées
              <strong> entre familles</strong>, semaine par semaine, selon les repas de chacun.
            </p>
            <p className="text-[11px] text-amber-700 font-semibold mt-1">
              ↗ = Montant si toutes les réservations provisoires deviennent confirmées
            </p>
          </div>
          <Link to="/comptes" className="hidden sm:inline-flex btn-ghost text-sm">
            Voir détail comptes →
          </Link>
        </div>

        {lignes.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            {/* Desktop: table large (text-xs + paddings serrés pour tenir dans le cadre) */}
            <div className="hidden md:block overflow-x-auto">
              <table className="table-clean text-xs lg:text-sm">
                <thead>
                  {/* Bandeau : séparation nette Maison (au tiers) / Courses (entre familles) */}
                  <tr className="text-[10px] uppercase tracking-wide">
                    <th className="px-3 py-1.5" />
                    <th className="px-2 py-1.5 text-center text-coral-800 bg-coral-50/70 border-b-2 border-coral-200"
                        colSpan={2 + semaines.length}>
                      🏠 Maison — à verser au propriétaire
                    </th>
                    <th className="px-2 py-1.5 text-center text-emerald-800 bg-emerald-50/70 border-b-2 border-emerald-200"
                        colSpan={4}>
                      🛒 Courses — à régler entre familles
                    </th>
                  </tr>
                  <tr>
                    <th className="text-left px-3 py-2">Famille</th>
                    <th className="text-right px-2 py-2 bg-coral-50/30">Nuits</th>
                    <th className="text-right px-2 py-2 bg-coral-50/30">Dû maison</th>
                    {semaines.map(sem => (
                      <th key={sem.index} className="text-right px-2 py-2 whitespace-nowrap bg-coral-50/30">
                        Sem. {sem.index + 1}
                      </th>
                    ))}
                    <th className="text-right px-2 py-2 bg-emerald-50/30">Repas</th>
                    <th className="text-right px-2 py-2 bg-emerald-50/30">Payé</th>
                    <th className="text-right px-2 py-2 bg-emerald-50/30">Dû courses</th>
                    <th className="text-right px-3 py-2 bg-emerald-50/30">Solde courses</th>
                  </tr>
                </thead>
                <tbody>
                  {lignes.map(l => {
                    const aTentatifs = l.nb_tentatifs > 0
                    const isOpen = openFamilles.has(l.compte_id)
                    const membres = membresParFamille.get(l.compte_id) ?? []
                    const parSem = netFamilleSemaine.get(l.compte_id) ?? []
                    return (
                      <Fragment key={l.compte_id}>
                        <tr className={isOpen ? 'bg-ocean-50/40' : ''}>
                          <td className="px-3 py-2">
                            <button
                              type="button"
                              onClick={() => toggleFamilleOpen(l.compte_id)}
                              className="flex items-center gap-1.5 font-semibold text-ocean-900 hover:text-coral-600 transition text-left"
                              aria-expanded={isOpen}
                              title="Afficher le détail des membres de la famille"
                            >
                              <span className="text-[10px] text-ocean-500">{isOpen ? '▾' : '▸'}</span>
                              <span className="underline decoration-dotted underline-offset-2">{l.compte_nom}</span>
                            </button>
                          </td>
                          <td className="px-2 py-2 text-right text-ocean-700 leading-tight">
                            {l.nb_nuits || '—'}
                            {aTentatifs && <div className="text-[10px] text-amber-700 font-semibold">↗ +{l.nb_tentatifs}</div>}
                          </td>
                          <td className="px-2 py-2 text-right leading-tight">
                            <div>{fmtEur(l.du_nuitees)}</div>
                            {aTentatifs && (
                              <div className="text-[10px] text-amber-700 font-semibold">↗ {fmtEur(l.du_nuitees_max)}</div>
                            )}
                          </td>
                          {semaines.map(sem => (
                            <td key={sem.index} className="px-2 py-2 text-right text-ocean-700">
                              {(parSem[sem.index] ?? 0) > 0.005 ? fmtEur(parSem[sem.index]) : '—'}
                            </td>
                          ))}
                          <td className="px-2 py-2 text-right text-ocean-700">{l.nb_repas || '—'}</td>
                          <td className="px-2 py-2 text-right text-ocean-700">{l.depenses_payees > 0 ? fmtEur(l.depenses_payees) : '—'}</td>
                          <td className="px-2 py-2 text-right">{l.du_repas > 0.005 ? fmtEur(l.du_repas) : '—'}</td>
                          <td className={`px-3 py-2 text-right font-bold leading-tight ${l.solde_courses < -0.005 ? 'text-coral-600' : 'text-ocean-900'}`}>
                            {Math.abs(l.solde_courses) > 0.005 ? fmtEur(l.solde_courses) : '—'}
                            {l.solde_courses < -0.005 && (
                              <div className="text-[9px] font-semibold text-coral-500 normal-case">on lui doit</div>
                            )}
                          </td>
                        </tr>
                        {isOpen && membres.map(m => (
                          <tr key={m.personne_id} className="bg-ocean-50/60 border-l-4 border-ocean-200">
                            <td className="pl-8 pr-3 py-1 text-ocean-800">
                              <span className="flex items-center gap-1.5">
                                <span className="font-medium">{m.prenom}</span>
                                <StatutTag categorie={m.categorie} />
                              </span>
                            </td>
                            <td className="px-2 py-1 text-right text-ocean-600 leading-tight">
                              {m.nb_nuits || '—'}
                              {m.nb_tent > 0 && <div className="text-[10px] text-amber-700">↗ +{m.nb_tent}</div>}
                            </td>
                            <td className="px-2 py-1 text-right text-ocean-600 leading-tight">
                              <div>{m.du_maison > 0.005 ? fmtEur(m.du_maison) : '—'}</div>
                              {m.nb_tent > 0 && (
                                <div className="text-[10px] text-amber-700">↗ {fmtEur(m.du_maison_max)}</div>
                              )}
                            </td>
                            {semaines.map(sem => (
                              <td key={sem.index} className="px-2 py-1 text-right text-ocean-600">
                                {(m.par_semaine[sem.index] ?? 0) > 0.005 ? fmtEur(m.par_semaine[sem.index]) : '—'}
                              </td>
                            ))}
                            <td className="px-2 py-1 text-right text-ocean-600">{m.nb_repas || '—'}</td>
                            <td className="px-2 py-1 text-right text-ocean-300">—</td>
                            <td className="px-2 py-1 text-right text-ocean-600">{m.du_repas > 0.005 ? fmtEur(m.du_repas) : '—'}</td>
                            <td className="px-3 py-1 text-right text-ocean-300">—</td>
                          </tr>
                        ))}
                      </Fragment>
                    )
                  })}
                </tbody>
                <tfoot className="bg-ocean-50 font-bold border-t-2 border-ocean-200">
                  <tr>
                    <td className="px-3 py-2 text-ocean-900 uppercase text-xs tracking-wide">TOTAL</td>
                    <td className="px-2 py-2 text-right text-ocean-900 leading-tight">
                      {totaux.nb_nuits}
                      {totaux.nb_tentatifs > 0 && <div className="text-[10px] text-amber-700">↗ +{totaux.nb_tentatifs}</div>}
                    </td>
                    <td className="px-2 py-2 text-right text-ocean-900 leading-tight">
                      <div>{fmtEur(totaux.du_nuitees)}</div>
                      {totaux.nb_tentatifs > 0 && (
                        <div className="text-[10px] text-amber-700">↗ {fmtEur(totaux.du_nuitees_max)}</div>
                      )}
                    </td>
                    {semaines.map(sem => (
                      <td key={sem.index} className="px-2 py-2 text-right text-ocean-900">
                        {fmtEur(sem.total_cout_net ?? sem.cout)}
                      </td>
                    ))}
                    <td className="px-2 py-2 text-right text-ocean-900">{totaux.nb_repas}</td>
                    <td className="px-2 py-2 text-right text-ocean-900">{fmtEur(totaux.depenses_payees)}</td>
                    <td className="px-2 py-2 text-right text-ocean-900">{fmtEur(totaux.du_repas)}</td>
                    <td className="px-3 py-2 text-right text-ocean-900">
                      {Math.abs(totaux.solde_courses) > 0.005 ? fmtEur(totaux.solde_courses) : fmtEur(0)}
                    </td>
                  </tr>
                </tfoot>
              </table>
              <p className="px-3 py-2 text-[11px] text-ocean-600/80 italic">
                Le <strong>dû maison</strong> se règle au propriétaire (total {fmtEur(totaux.du_nuitees)}).
                Le <strong>solde courses</strong> s'équilibre entre familles (sa somme vaut 0) —
                voir les virements dans <Link to="/comptes" className="underline hover:text-coral-600">Comptes</Link>.
              </p>
            </div>

            {/* Mobile: cartes empilées */}
            <div className="md:hidden divide-y divide-ocean-50">
              {lignes.map(l => {
                const aTentatifs = l.nb_tentatifs > 0
                const isOpen = openFamilles.has(l.compte_id)
                const membres = membresParFamille.get(l.compte_id) ?? []
                const parSem = netFamilleSemaine.get(l.compte_id) ?? []
                return (
                  <div key={l.compte_id} className="px-4 py-3">
                    <div className="flex items-baseline justify-between mb-1.5">
                      <button
                        type="button"
                        onClick={() => toggleFamilleOpen(l.compte_id)}
                        className="flex items-center gap-1.5 font-bold text-ocean-900 text-left"
                        aria-expanded={isOpen}
                      >
                        <span className="text-[10px] text-ocean-500">{isOpen ? '▾' : '▸'}</span>
                        <span className="underline decoration-dotted underline-offset-2">{l.compte_nom}</span>
                      </button>
                      <div className="text-right leading-tight">
                        <span className={`text-base font-bold ${l.solde_courses < -0.005 ? 'text-coral-600' : 'text-ocean-900'}`}>
                          {Math.abs(l.solde_courses) > 0.005 ? fmtEur(l.solde_courses) : '—'}
                        </span>
                        <div className="text-[9px] text-ocean-500 uppercase tracking-wide">
                          {l.solde_courses < -0.005 ? 'courses : on lui doit' : 'solde courses'}
                        </div>
                      </div>
                    </div>
                    {/* 🏠 Maison — au propriétaire */}
                    <div className="rounded-lg bg-coral-50/50 border border-coral-100 px-2.5 py-1.5 mb-1.5">
                      <div className="text-[10px] uppercase tracking-wide font-bold text-coral-700 mb-0.5">🏠 Maison — au propriétaire</div>
                      <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs">
                        <Kv k="Nuits" v={l.nb_nuits ? `${l.nb_nuits}${aTentatifs ? ` (+${l.nb_tentatifs}↔)` : ''}` : '—'} />
                        <Kv k="Dû maison" v={fmtEur(l.du_nuitees)} extra={aTentatifs ? `↗ ${fmtEur(l.du_nuitees_max)}` : undefined} />
                        {semaines.map(sem => (
                          <Kv
                            key={sem.index}
                            k={`Semaine ${sem.index + 1}`}
                            v={(parSem[sem.index] ?? 0) > 0.005 ? fmtEur(parSem[sem.index]) : '—'}
                          />
                        ))}
                      </div>
                    </div>
                    {/* 🛒 Courses — entre familles */}
                    <div className="rounded-lg bg-emerald-50/50 border border-emerald-100 px-2.5 py-1.5">
                      <div className="text-[10px] uppercase tracking-wide font-bold text-emerald-700 mb-0.5">🛒 Courses — entre familles</div>
                      <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs">
                        <Kv k="Repas" v={`${l.nb_repas || '—'}`} />
                        <Kv k="Payé" v={l.depenses_payees > 0 ? fmtEur(l.depenses_payees) : '—'} />
                        <Kv k="Dû courses" v={l.du_repas > 0.005 ? fmtEur(l.du_repas) : '—'} />
                        <Kv
                          k="Solde courses"
                          v={Math.abs(l.solde_courses) > 0.005 ? fmtEur(l.solde_courses) : '—'}
                        />
                      </div>
                    </div>

                    {/* Détail des membres de la famille */}
                    {isOpen && (
                      <div className="mt-2 space-y-1.5 anim-in">
                        {membres.map(m => (
                          <div key={m.personne_id} className="rounded-lg border border-ocean-100 bg-ocean-50/50 px-2.5 py-1.5">
                            <div className="flex items-center justify-between gap-2">
                              <span className="flex items-center gap-1.5 min-w-0">
                                <span className="font-semibold text-ocean-900 text-sm truncate">{m.prenom}</span>
                                <StatutTag categorie={m.categorie} />
                              </span>
                              <span className="text-xs text-ocean-600 shrink-0">
                                🏠 {m.du_maison > 0.005 ? fmtEur(m.du_maison) : '—'}
                              </span>
                            </div>
                            <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5 text-[11px] text-ocean-700">
                              <span>🌙 {m.nb_nuits || 0} nuit{m.nb_nuits > 1 ? 's' : ''}{m.nb_tent > 0 ? ` (+${m.nb_tent}↔)` : ''}</span>
                              {semaines.map(sem => (
                                (m.par_semaine[sem.index] ?? 0) > 0.005 && (
                                  <span key={sem.index}>S{sem.index + 1} : {fmtEur(m.par_semaine[sem.index])}</span>
                                )
                              ))}
                              <span>🍽️ {m.nb_repas || 0} repas</span>
                              <span>🛒 dû {m.du_repas > 0.005 ? fmtEur(m.du_repas) : '—'}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )
              })}
              {/* Ligne TOTAL mobile */}
              <div className="px-4 py-3 bg-ocean-50 border-t-2 border-ocean-200">
                <h3 className="font-bold text-ocean-900 uppercase tracking-wide text-sm mb-1.5">TOTAL</h3>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                  <Kv k="🏠 Nuits" v={`${totaux.nb_nuits}${totaux.nb_tentatifs > 0 ? ` (+${totaux.nb_tentatifs}↔)` : ''}`} />
                  <Kv k="🏠 Dû maison" v={fmtEur(totaux.du_nuitees)} extra={totaux.nb_tentatifs > 0 ? `↗ ${fmtEur(totaux.du_nuitees_max)}` : undefined} />
                  <Kv k="🛒 Repas" v={`${totaux.nb_repas}`} />
                  <Kv k="🛒 Payé" v={fmtEur(totaux.depenses_payees)} />
                  <Kv k="🛒 Dû courses" v={fmtEur(totaux.du_repas)} />
                  <Kv k="🛒 Solde courses" v={Math.abs(totaux.solde_courses) > 0.005 ? fmtEur(totaux.solde_courses) : fmtEur(0)} />
                </div>
              </div>
            </div>
          </>
        )}
      </section>

      {/* === Répartition du coût par semaine === */}
      {semaines.length > 0 && (
        <section className="card overflow-hidden">
          <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-ocean-100/60">
            <h2 className="text-lg sm:text-xl font-bold text-ocean-900 flex items-center gap-2">
              <span>💰</span>
              <span>Coût de la maison par semaine</span>
            </h2>
            <p className="text-xs sm:text-sm text-ocean-700 mt-0.5">
              Chaque semaine dispose d'un budget fixe de <strong>{fmtEur(coutSemaine)}</strong>,
              réparti <strong>indépendamment</strong> entre les personnes présentes cette semaine-là.
              Le prix d'une nuitée dépend donc du nombre de personnes présentes sur la semaine.
            </p>
          </div>
          <div className="p-3 sm:p-4 grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
            {semaines.map(sem => (
              <SemaineCard key={sem.index} semaine={sem} />
            ))}
          </div>
        </section>
      )}

      {/* === Planning des nuits — jauge capacité === */}
      {planning.length > 0 && (
        <section className="card overflow-hidden">
          <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-ocean-100/60">
            <h2 className="text-lg sm:text-xl font-bold text-ocean-900 flex items-center gap-2">
              <span>📅</span>
              <span>Planning des nuits</span>
            </h2>
            <p className="text-xs sm:text-sm text-ocean-700 mt-0.5">
              Nombre de personnes présentes par nuit sur les <strong>{capaciteTotale} couchages</strong> de la maison.
            </p>
            <div className="flex flex-wrap gap-3 mt-2 text-[10px] text-ocean-700">
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-emerald-500" />OK</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-amber-300" />Peut-être</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-orange-500" />Presque plein (≥ 80 %)</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-coral-500" />Capacité dépassée (avec provisoires)</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-ocean-200" />Aucun inscrit</span>
            </div>
          </div>

          <div className="p-3 sm:p-4 grid grid-cols-7 gap-1.5 sm:gap-2">
            {planning.map(({ nuit, nb_confirmes, nb_peut_etre }) => (
              <PlanningCell
                key={nuit.id}
                nuit={nuit}
                nbConfirmes={nb_confirmes}
                nbPeutEtre={nb_peut_etre}
                capacite={capaciteTotale}
                isCouchageAlerte={nuitsEnSurcapacite.has(nuit.id)}
              />
            ))}
          </div>
        </section>
      )}

      {/* === Détail des coûts de la maison === */}
      {semaines.length > 0 && (
        <DetailCoutsMaison semaines={semaines} coutSemaine={coutSemaine} />
      )}

    </div>
  )
}

/* ============================================================================
   Détail des coûts de la maison — explication pas à pas du calcul
   ========================================================================= */
function DetailCoutsMaison({ semaines, coutSemaine }: {
  semaines: SemaineNuitees[]; coutSemaine: number
}) {
  // Toutes les semaines sont DÉPLIÉES par défaut — l'utilisateur peut les replier.
  const [openSemaines, setOpenSemaines] = useState<Set<number>>(
    () => new Set(semaines.map(s => s.index)),
  )
  const toggleSemaine = (idx: number) =>
    setOpenSemaines(s => {
      const n = new Set(s)
      n.has(idx) ? n.delete(idx) : n.add(idx)
      return n
    })
  // Familles dépliées (clic sur le nom du responsable) — clé "semaine-compte"
  const [openFam, setOpenFam] = useState<Set<string>>(new Set())
  const toggleFam = (key: string) =>
    setOpenFam(s => {
      const n = new Set(s)
      n.has(key) ? n.delete(key) : n.add(key)
      return n
    })

  return (
    <section className="card overflow-hidden">
      <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-ocean-100/60">
        <h2 className="text-lg sm:text-xl font-bold text-ocean-900 flex items-center gap-2">
          <span>🧾</span>
          <span>Détail des coûts de la maison</span>
        </h2>
        <p className="text-xs sm:text-sm text-ocean-700 mt-0.5">
          Comment le montant de chacun est obtenu, semaine par semaine.
        </p>
      </div>

      {/* Principe du calcul — 4 étapes */}
      <div className="px-4 sm:px-5 py-3 bg-gradient-sand text-sand-800">
        <h3 className="font-bold text-sm mb-2">📐 Le principe, en 4 étapes</h3>
        <ol className="space-y-1.5 text-xs sm:text-sm list-none">
          <li className="grid grid-cols-[22px_1fr] gap-2 items-start">
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-sand-300 text-sand-900 font-bold text-[10px]">1</span>
            <span>
              Chaque semaine a un <strong>budget fixe de {fmtEur(coutSemaine)}</strong>,
              totalement indépendant de l'autre semaine.
            </span>
          </li>
          <li className="grid grid-cols-[22px_1fr] gap-2 items-start">
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-sand-300 text-sand-900 font-bold text-[10px]">2</span>
            <span>
              <strong>Coût brut</strong> — on compte <strong>toutes les nuitées de la semaine</strong>,
              tous statuts confondus (1 personne × 1 nuit = 1 nuitée). Chacun paie alors
              <strong> ses nuitées ÷ nuitées totales × {fmtEur(coutSemaine)}</strong> — donc
              strictement <strong>au prorata de sa présence</strong>.
            </span>
          </li>
          <li className="grid grid-cols-[22px_1fr] gap-2 items-start">
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-sand-300 text-sand-900 font-bold text-[10px]">3</span>
            <span>
              <strong>Selon statut</strong> — on applique la réduction liée au statut&nbsp;:
              <span className="inline-flex flex-wrap gap-1 ml-1">
                <span className="px-1.5 py-0.5 rounded bg-ocean-100 text-ocean-800 font-semibold text-[10px]">Adulte / Ado&nbsp;: 100 %</span>
                <span className="px-1.5 py-0.5 rounded bg-sand-200 text-sand-900 font-semibold text-[10px]">Enfant&nbsp;: 80 %</span>
                <span className="px-1.5 py-0.5 rounded bg-coral-100 text-coral-800 font-semibold text-[10px]">Bébé&nbsp;: 0 %</span>
              </span>
            </span>
          </li>
          <li className="grid grid-cols-[22px_1fr] gap-2 items-start">
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-coral-300 text-coral-900 font-bold text-[10px]">4</span>
            <span>
              <strong>Surcoût adulte</strong> — les réductions accordées (20 % de chaque enfant,
              100 % de chaque bébé) créent un manque à gagner, réparti <strong>uniquement entre
              les adultes / ados de la même semaine</strong>, au prorata de <strong>leurs seules
              nuitées</strong> (et non du total). Enfants et bébés ne supportent aucun surcoût, et
              un enfant présent en semaine 1 n'impacte jamais les adultes de la semaine 2.
            </span>
          </li>
        </ol>
      </div>

      {/* Détail par semaine (accordéon) */}
      <div className="divide-y divide-ocean-100">
        {semaines.map(sem => {
          const isOpen = openSemaines.has(sem.index)
          const totalSem = sem.detail.reduce((s, d) => s + d.montant_final, 0)
          return (
            <div key={sem.index}>
              <button
                type="button"
                onClick={() => toggleSemaine(sem.index)}
                className="w-full px-4 sm:px-5 py-3 flex items-center justify-between gap-2 text-left hover:bg-ocean-50/50 transition"
                aria-expanded={isOpen}
              >
                <div className="min-w-0">
                  <div className="font-bold text-ocean-900 text-sm sm:text-base">
                    {sem.libelle}
                    <span className="ml-2 font-normal text-ocean-700 text-xs">
                      · {sem.nb_nuitees_total} nuitée{sem.nb_nuitees_total > 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="text-[11px] sm:text-xs text-ocean-700/80 mt-0.5">
                    Tarif de base <strong>{fmtEur(sem.base_rate)}</strong>/nuitée
                    {sem.montant_redistribue > 0.005 && (
                      <> · report enfants&nbsp;/&nbsp;bébés <strong className="text-coral-700">{fmtEur(sem.montant_redistribue)}</strong></>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="px-2 py-0.5 rounded-full bg-coral-100 text-coral-800 text-xs font-bold">
                    {fmtEur(totalSem)}
                  </span>
                  <span className="text-ocean-600">{isOpen ? '▾' : '▸'}</span>
                </div>
              </button>

              {isOpen && (
                <div className="px-3 sm:px-5 pb-4 anim-in">
                  {sem.detail.length === 0 ? (
                    <p className="text-sm italic text-ocean-500 py-3">
                      Aucune présence confirmée sur cette semaine — elle n'est pas facturée.
                    </p>
                  ) : (
                    <>
                      {/* Formule chiffrée de la semaine */}
                      <div className="text-[11px] sm:text-xs bg-ocean-50 border border-ocean-100 rounded-lg px-3 py-2 mb-3 text-ocean-800 leading-relaxed space-y-1">
                        <div>
                          <span className="font-bold text-ocean-900">Coût brut&nbsp;:</span>{' '}
                          <strong>{fmtEur(sem.cout)}</strong> ÷ <strong>{sem.nb_nuitees_total}</strong> nuitées
                          = <strong>{fmtEur(sem.base_rate)}</strong> par nuitée.
                        </div>
                        {sem.montant_redistribue > 0.005 && (
                          <div>
                            <span className="font-bold text-coral-800">Surcoût adulte&nbsp;:</span>{' '}
                            les réductions enfants&nbsp;/&nbsp;bébés représentent{' '}
                            <strong className="text-coral-700">{fmtEur(sem.montant_redistribue)}</strong>, répartis sur
                            les <strong>{sem.nb_nuitees_adulte_ado}</strong> nuitées adulte&nbsp;/&nbsp;ado
                            {' '}(et non sur les {sem.nb_nuitees_total} nuitées totales), soit{' '}
                            <strong className="text-coral-700">
                              {fmtEur(sem.nb_nuitees_adulte_ado > 0 ? sem.montant_redistribue / sem.nb_nuitees_adulte_ado : 0)}
                            </strong> par nuitée adulte.
                          </div>
                        )}
                      </div>

                      {/* === Coût par famille (colonnes E et K) — cliquer une famille
                             déplie le détail de ses membres avec TOUTES les colonnes === */}
                      <h4 className="text-[11px] uppercase tracking-wide font-bold text-ocean-700 mb-1.5">
                        Coût par famille — {sem.libelle}
                        <span className="ml-2 normal-case font-normal text-ocean-500">
                          (cliquer sur une famille pour voir le détail de ses membres)
                        </span>
                      </h4>

                      {/* Desktop — le tableau replié n'affiche QUE Nuitées + Coût net.
                          Le coût brut et le détail complet apparaissent au clic sur la famille. */}
                      <div className="hidden md:block overflow-x-auto">
                        <table className="table-clean">
                          <thead>
                            <tr>
                              <th className="text-left px-3 py-2">Famille</th>
                              <th className="text-right px-2 py-2">Nuitées</th>
                              <th className="text-right px-3 py-2">Coût net famille</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(sem.familles ?? []).map(f => {
                              const key = `${sem.index}-${f.compte_id}`
                              const isFamOpen = openFam.has(key)
                              const membres = sem.detail.filter(d => d.compte_id === f.compte_id)
                              return (
                                <Fragment key={f.compte_id}>
                                  <tr className={isFamOpen ? 'bg-ocean-50/40' : ''}>
                                    <td className="px-3 py-1.5">
                                      <button
                                        type="button"
                                        onClick={() => toggleFam(key)}
                                        className="flex items-center gap-1.5 font-semibold text-ocean-900 hover:text-coral-600 transition text-left"
                                        aria-expanded={isFamOpen}
                                        title="Afficher le détail des membres"
                                      >
                                        <span className="text-[10px] text-ocean-500">{isFamOpen ? '▾' : '▸'}</span>
                                        <span className="underline decoration-dotted underline-offset-2">{f.compte_nom}</span>
                                      </button>
                                    </td>
                                    <td className="px-2 py-1.5 text-right text-ocean-800">{f.nb_nuits}</td>
                                    <td className="px-3 py-1.5 text-right font-bold text-ocean-900">{fmtEur(f.cout_net)}</td>
                                  </tr>
                                  {isFamOpen && (
                                    <tr>
                                      <td colSpan={3} className="px-2 pb-3 pt-1 bg-ocean-50/40">
                                        <div className="overflow-x-auto rounded-lg border border-ocean-100 bg-white">
                                          <table className="table-clean text-xs">
                                            <thead>
                                              <tr>
                                                <th className="text-left px-3 py-1.5">Personne</th>
                                                <th className="text-left px-2 py-1.5">Famille</th>
                                                <th className="text-center px-2 py-1.5">Statut</th>
                                                <th className="text-right px-2 py-1.5">Nuitées</th>
                                                <th className="text-right px-2 py-1.5">Coût brut</th>
                                                <th className="text-center px-2 py-1.5">Selon statut</th>
                                                <th className="text-right px-2 py-1.5">Coût selon statut</th>
                                                <th className="text-right px-2 py-1.5">Surcoût adulte</th>
                                                <th className="text-right px-3 py-1.5">Coût net total</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {membres.map(d => (
                                                <tr key={d.personne_id}>
                                                  <td className="px-3 py-1 font-semibold text-ocean-900">{d.prenom}</td>
                                                  <td className="px-2 py-1 text-ocean-600">{d.compte_nom}</td>
                                                  <td className="px-2 py-1 text-center"><StatutTag categorie={d.categorie} /></td>
                                                  <td className="px-2 py-1 text-right text-ocean-800">{d.nb_nuits}</td>
                                                  <td className="px-2 py-1 text-right text-ocean-600">{fmtEur(d.montant_base)}</td>
                                                  <td className="px-2 py-1 text-center text-ocean-700 font-semibold">{Math.round(d.coefficient * 100)} %</td>
                                                  <td className="px-2 py-1 text-right text-ocean-700">{fmtEur(d.montant_apres_coeff)}</td>
                                                  <td className="px-2 py-1 text-right">
                                                    {d.part_redistribuee > 0.005
                                                      ? <span className="text-coral-700 font-semibold">+{fmtEur(d.part_redistribuee)}</span>
                                                      : <span className="text-ocean-300">—</span>}
                                                  </td>
                                                  <td className="px-3 py-1 text-right font-bold text-ocean-900">{fmtEur(d.montant_final)}</td>
                                                </tr>
                                              ))}
                                              <tr className="bg-ocean-50/70 font-bold">
                                                <td className="px-3 py-1 text-ocean-900" colSpan={3}>Total {f.compte_nom}</td>
                                                <td className="px-2 py-1 text-right text-ocean-900">{f.nb_nuits}</td>
                                                <td className="px-2 py-1 text-right text-ocean-900">{fmtEur(f.cout_brut)}</td>
                                                <td className="px-2 py-1" />
                                                <td className="px-2 py-1 text-right text-ocean-900">
                                                  {fmtEur(membres.reduce((s, d) => s + d.montant_apres_coeff, 0))}
                                                </td>
                                                <td className="px-2 py-1 text-right text-coral-700">
                                                  {membres.reduce((s, d) => s + d.part_redistribuee, 0) > 0.005
                                                    ? `+${fmtEur(membres.reduce((s, d) => s + d.part_redistribuee, 0))}`
                                                    : '—'}
                                                </td>
                                                <td className="px-3 py-1 text-right text-ocean-900">{fmtEur(f.cout_net)}</td>
                                              </tr>
                                            </tbody>
                                          </table>
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                </Fragment>
                              )
                            })}
                          </tbody>
                          <tfoot className="bg-ocean-50/80">
                            <tr>
                              <td className="px-3 py-2 font-bold text-ocean-900">TOTAL {sem.libelle}</td>
                              <td className="px-2 py-2 text-right font-bold text-ocean-900">{sem.nb_nuitees_total}</td>
                              <td className="px-3 py-2 text-right font-bold text-ocean-900">
                                {fmtEur((sem.familles ?? []).reduce((s, f) => s + f.cout_net, 0))}
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>

                      {/* Mobile : cartes famille dépliables */}
                      <div className="md:hidden space-y-1.5">
                        {(sem.familles ?? []).map(f => {
                          const key = `${sem.index}-${f.compte_id}`
                          const isFamOpen = openFam.has(key)
                          const membres = sem.detail.filter(d => d.compte_id === f.compte_id)
                          return (
                            <div key={f.compte_id} className="border border-ocean-100 rounded-lg bg-white overflow-hidden">
                              <button
                                type="button"
                                onClick={() => toggleFam(key)}
                                className="w-full px-3 py-2 flex items-center justify-between gap-2 text-left hover:bg-ocean-50/50 transition"
                                aria-expanded={isFamOpen}
                              >
                                <span className="flex items-center gap-1.5 min-w-0">
                                  <span className="text-[10px] text-ocean-500">{isFamOpen ? '▾' : '▸'}</span>
                                  <span className="font-bold text-ocean-900 truncate underline decoration-dotted underline-offset-2">{f.compte_nom}</span>
                                  <span className="text-[10px] text-ocean-500 shrink-0">{f.nb_nuits} nuitée{f.nb_nuits > 1 ? 's' : ''}</span>
                                </span>
                                <span className="font-bold text-ocean-900 shrink-0">{fmtEur(f.cout_net)}</span>
                              </button>
                              {isFamOpen && (
                                <div className="px-3 pb-2 space-y-1 anim-in border-t border-ocean-50">
                                  <div className="text-[10px] text-ocean-500 pt-1.5">
                                    Coût brut famille : <strong className="text-ocean-700">{fmtEur(f.cout_brut)}</strong>
                                    {' '}· Coût net famille : <strong className="text-ocean-700">{fmtEur(f.cout_net)}</strong>
                                  </div>
                                  {membres.map(d => (
                                    <div key={d.personne_id} className="rounded-md bg-ocean-50/60 border border-ocean-100 px-2.5 py-1.5">
                                      <div className="flex items-center justify-between gap-2">
                                        <span className="flex items-center gap-1.5 min-w-0">
                                          <span className="font-semibold text-ocean-900 truncate">{d.prenom}</span>
                                          <StatutTag categorie={d.categorie} />
                                        </span>
                                        <span className="font-bold text-ocean-900 shrink-0">{fmtEur(d.montant_final)}</span>
                                      </div>
                                      <div className="text-[10px] text-ocean-700 mt-0.5 leading-snug">
                                        {d.nb_nuits} nuitée{d.nb_nuits > 1 ? 's' : ''} · brut {fmtEur(d.montant_base)} ×
                                        {' '}{Math.round(d.coefficient * 100)} % = {fmtEur(d.montant_apres_coeff)}
                                        {d.part_redistribuee > 0.005 && (
                                          <span className="text-coral-700 font-semibold"> + surcoût {fmtEur(d.part_redistribuee)}</span>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )
                        })}
                        <div className="flex items-baseline justify-between px-3 py-2 bg-ocean-50 rounded-lg font-bold text-ocean-900">
                          <span>TOTAL {sem.libelle}</span>
                          <span>{fmtEur(totalSem)}</span>
                        </div>
                      </div>

                      <p className="text-[10px] text-emerald-700 font-semibold mt-2 flex items-center gap-1">
                        <span aria-hidden>✓</span>
                        Contrôle : coût brut total et coût net total valent bien {fmtEur(sem.cout)}.
                      </p>
                    </>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </section>
  )
}

function StatutTag({ categorie }: { categorie: Categorie }) {
  const cfg: Record<Categorie, { label: string; cls: string }> = {
    adulte: { label: 'Adulte', cls: 'bg-ocean-100 text-ocean-800' },
    adolescent: { label: 'Ado', cls: 'bg-emerald-100 text-emerald-800' },
    enfant: { label: 'Enfant', cls: 'bg-sand-200 text-sand-900' },
    bebe: { label: 'Bébé', cls: 'bg-coral-100 text-coral-700' },
  }
  const c = cfg[categorie]
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${c.cls}`}>
      {c.label}
    </span>
  )
}

function PlanningCell({ nuit, nbConfirmes, nbPeutEtre, capacite, isCouchageAlerte }: {
  nuit: Nuit; nbConfirmes: number; nbPeutEtre: number; capacite: number;
  isCouchageAlerte: boolean
}) {
  const d = new Date(nuit.date_debut)
  const total = nbConfirmes + nbPeutEtre
  const ratio = capacite > 0 ? nbConfirmes / capacite : 0
  const pctConfirmes = Math.min(100, ratio * 100)
  const pctTotalPotential = capacite > 0 ? Math.min(100, (total / capacite) * 100) : 0
  // Surcapacité STRICTE (confirmés seuls) → rouge fort.
  const isOverConf = nbConfirmes > capacite
  // Surcapacité POTENTIELLE (confirmés + provisoires) → rouge également, badge ⚠.
  const isOverWithTent = total > capacite
  // Alerte côté Couchages (sur_capacite par chambre, non_assigné…) — règle BACKEND
  // identique à celle du sélecteur de l'onglet Couchages.
  const isOver = isOverConf || isOverWithTent || isCouchageAlerte
  const isHigh = ratio >= 0.8 && !isOver
  const isEmpty = total === 0

  const cardCls = isOver
    ? 'bg-coral-50 border-coral-300 text-coral-900'
    : isHigh
    ? 'bg-orange-50 border-orange-300 text-orange-900'
    : isEmpty
    ? 'bg-ocean-50/60 border-ocean-100 text-ocean-700/70'
    : 'bg-emerald-50 border-emerald-200 text-emerald-900'

  const barCls = isOver
    ? 'bg-coral-500'
    : isHigh
    ? 'bg-orange-500'
    : 'bg-emerald-500'

  const tooltipParts = [
    `${nuit.libelle}`,
    `${nbConfirmes} confirmé${nbConfirmes > 1 ? 's' : ''}`,
  ]
  if (nbPeutEtre > 0) tooltipParts.push(`+${nbPeutEtre} peut-être`)
  tooltipParts.push(`/ ${capacite} couchages`)
  if (isOverConf) tooltipParts.push('⚠ trop de confirmés')
  else if (isOverWithTent) tooltipParts.push('⚠ trop de monde avec les provisoires')
  else if (isCouchageAlerte) tooltipParts.push('⚠ alerte couchages (voir onglet Couchages)')

  return (
    <div
      title={tooltipParts.join(' · ')}
      className={`relative rounded-lg border ${cardCls} p-1 sm:p-1.5 flex flex-col items-center justify-between min-h-[78px] sm:min-h-[90px] transition`}
    >
      <div className="text-[9px] sm:text-[10px] uppercase font-bold opacity-80 leading-none">
        {JOURS_COURT[d.getDay()]}
      </div>
      <div className="text-base sm:text-xl font-bold leading-none">
        {d.getDate()}
      </div>
      {/* Barre : couche 'peut-être' (jaune transparent dessous) + couche 'confirmés' (couleur principale dessus) */}
      <div className="w-full h-1 sm:h-1.5 bg-white/70 rounded-full overflow-hidden my-0.5 relative">
        {nbPeutEtre > 0 && (
          <div
            className="absolute inset-y-0 left-0 bg-amber-300/70"
            style={{ width: `${pctTotalPotential}%` }}
          />
        )}
        <div
          className={`absolute inset-y-0 left-0 ${barCls} transition-all duration-300`}
          style={{ width: `${pctConfirmes}%` }}
        />
      </div>
      <div className="text-[10px] sm:text-[11px] font-bold leading-none">
        {nbConfirmes}
        {nbPeutEtre > 0 && <span className="text-amber-700"> +{nbPeutEtre}↔</span>}
        <span className="opacity-60">/{capacite}</span>
      </div>
      {isOver && (
        <span className="absolute -top-1.5 -right-1 text-[10px] bg-coral-500 text-white rounded-full px-1 leading-tight shadow-sm">⚠</span>
      )}
    </div>
  )
}

function StatCard({ label, value, sub, accent, icon }: {
  label: string; value: string; sub?: string;
  accent: 'ocean' | 'coral' | 'sand' | 'sunset'; icon: string
}) {
  const bg = {
    ocean: 'from-ocean-50 to-ocean-100',
    coral: 'from-coral-50 to-coral-100',
    sand: 'from-sand-50 to-sand-100',
    sunset: 'from-orange-50 to-amber-100',
  }[accent]
  const text = {
    ocean: 'text-ocean-900',
    coral: 'text-coral-800',
    sand: 'text-sand-800',
    sunset: 'text-sunset-600',
  }[accent]
  return (
    <div className={`relative rounded-2xl p-3 sm:p-4 bg-gradient-to-br ${bg} border border-white/40 shadow-soft overflow-hidden card-hover`}>
      <div className="text-2xl absolute top-2 right-2 opacity-30">{icon}</div>
      <div className="text-[10px] sm:text-xs font-bold text-ocean-700/80 uppercase tracking-wide">{label}</div>
      <div className={`text-lg sm:text-2xl font-bold mt-1 ${text}`}>{value}</div>
      {sub && <div className="text-[10px] sm:text-xs text-ocean-700/80 mt-0.5">{sub}</div>}
    </div>
  )
}

function Kv({ k, v, extra }: { k: string; v: string; extra?: string }) {
  return (
    <div className="flex justify-between items-baseline">
      <span className="text-ocean-600">{k}</span>
      <span className="text-right leading-tight">
        <span className="font-medium text-ocean-900">{v}</span>
        {extra && <div className="text-[10px] text-amber-700 font-semibold">{extra}</div>}
      </span>
    </div>
  )
}

function EmptyState() {
  return (
    <div className="px-4 py-10 text-center text-ocean-700">
      <div className="text-4xl mb-2">⛱️</div>
      <p className="font-semibold text-ocean-900">Personne n'a encore saisi sa présence.</p>
      <p className="text-sm mt-1">
        Commencez par <Link to="/nuitees" className="text-coral-600 hover:underline font-semibold">cocher vos nuits</Link>,
        puis <Link to="/repas" className="text-coral-600 hover:underline font-semibold">les repas</Link>.
      </p>
    </div>
  )
}

/** Carte récap d'une semaine du séjour : montant fixe + décompte nuitées + prix moyen. */
function SemaineCard({ semaine }: { semaine: SemaineNuitees }) {
  const fmtDate = (iso: string) => {
    const d = new Date(iso)
    return d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })
  }
  const nbTotal = semaine.nb_nuitees_adulte_ado + semaine.nb_nuitees_enfant + semaine.nb_nuitees_bebe
  // Prix RÉELS calculés côté backend. Attention : le prix enfant n'est PAS
  // « prix adulte × 0.8 » — l'enfant paie 80 % du TARIF DE BASE, tandis que
  // l'adulte paie la base + le report du reliquat enfants/bébés.
  const prixAdulte = semaine.prix_nuit_adulte ?? semaine.prix_par_poids
  const prixEnfant = semaine.prix_nuit_enfant ?? 0

  return (
    <div className="rounded-xl border-2 border-ocean-100 bg-gradient-to-br from-white to-ocean-50/60 p-3 sm:p-4">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div>
          <h3 className="font-bold text-ocean-900 text-base sm:text-lg leading-tight">
            {semaine.libelle}
          </h3>
          <p className="text-[11px] sm:text-xs text-ocean-700/80 mt-0.5">
            {fmtDate(semaine.date_debut)} → {fmtDate(semaine.date_fin)}
          </p>
        </div>
        <div className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-coral-100 text-coral-800 text-xs sm:text-sm font-bold">
          {semaine.cout.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })}
        </div>
      </div>

      {/* Décompte nuitées par catégorie */}
      <div className="grid grid-cols-3 gap-1.5 mt-3">
        <NuiteesTag icon="🧑" label="Adulte/Ado" nb={semaine.nb_nuitees_adulte_ado} accent="ocean" />
        <NuiteesTag icon="🧒" label="Enfant" nb={semaine.nb_nuitees_enfant} accent="sand" />
        <NuiteesTag icon="👶" label="Bébé" nb={semaine.nb_nuitees_bebe} accent="coral" />
      </div>
      <p className="text-[10px] text-ocean-600/70 italic mt-1.5 text-center">
        {nbTotal > 0 ? `${nbTotal} nuitée${nbTotal > 1 ? 's' : ''} confirmée${nbTotal > 1 ? 's' : ''} cette semaine`
          : 'aucune nuitée confirmée cette semaine'}
      </p>

      {/* Coût moyen par nuit */}
      <div className="mt-3 pt-3 border-t border-ocean-100/70">
        <div className="text-[10px] uppercase tracking-wide text-ocean-700 font-bold mb-1">
          Coût moyen par nuit
        </div>
        {semaine.poids_total > 0 ? (
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="bg-white rounded-md border border-ocean-100 px-2 py-1.5 leading-tight">
              <div className="text-[10px] text-ocean-600/80 font-semibold">Adulte / Ado</div>
              <div className="font-bold text-ocean-900">
                {prixAdulte.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 })}
              </div>
            </div>
            <div className="bg-white rounded-md border border-ocean-100 px-2 py-1.5 leading-tight">
              <div className="text-[10px] text-ocean-600/80 font-semibold">Enfant</div>
              <div className="font-bold text-ocean-900">
                {prixEnfant.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 })}
              </div>
            </div>
          </div>
        ) : (
          <p className="text-xs italic text-ocean-500">
            Pas encore facturable — aucun confirmé cette semaine.
          </p>
        )}
      </div>
    </div>
  )
}

function NuiteesTag({ icon, label, nb, accent }: {
  icon: string; label: string; nb: number; accent: 'ocean' | 'sand' | 'coral'
}) {
  const cls = accent === 'ocean'
    ? 'bg-ocean-50 border-ocean-200 text-ocean-800'
    : accent === 'sand'
    ? 'bg-sand-50 border-sand-200 text-sand-800'
    : 'bg-coral-50 border-coral-200 text-coral-800'
  return (
    <div className={`rounded-md border ${cls} px-2 py-1.5 text-center leading-tight`}>
      <div className="text-[10px] font-semibold flex items-center justify-center gap-1">
        <span aria-hidden>{icon}</span>
        <span>{label}</span>
      </div>
      <div className="text-lg font-bold">{nb}</div>
    </div>
  )
}
